import ErrorState from '@Guidelines/Component/ErrorState';
import PageHeader from '@Guidelines/Layout/PageHeader';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import useUploadSubject from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/Hooks/useUploadSubject';
import InterviewUploadSkeleton from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/InterviewUploadSkeleton';
import AwaitingServerCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/TransferCard/AwaitingServerCard';
import TransferCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/TransferCard';
import InterruptedRecordingBanner from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/InterruptedRecordingBanner';
import OrphanedInterviewBanner from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/OrphanedInterviewBanner';
import UploadEmptyState from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/UploadEmptyState';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type { InterviewDetailsSource } from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail/resolveInterviewDetail';
import { CheckCircle2, MicOff, SearchX, Settings2, UploadCloud } from 'lucide-react';
import * as React from 'react';
import { Navigate } from 'react-router-dom';

type Props = {
	readonly subject: SubjectAddress;
	readonly source: InterviewDetailsSource;
};

/**
 * Reads where the interview stands and shows the one card that answers it. The server is followed
 * by the dossier event stream, which replaces the interview's row; a running transfer is followed
 * on the queue of this device, which changes by itself.
 */
const InterviewUploadContent: React.FC<Props> = ({ subject, source }) => {
	const { stage, kind, title, participants, error, refetch } = useUploadSubject(subject, source);

	if (stage.type === 'loading') {
		return <InterviewUploadSkeleton />;
	}

	if (stage.type === 'processing') {
		return <Navigate to={`${buildSubjectPath(subject)}/interviews/${stage.interviewId}`} replace />;
	}

	const renderStageCard = (): React.ReactNode => {
		switch (stage.type) {
			case 'orphaned':
				return (
					<OrphanedInterviewBanner
						subject={subject}
						outboxItemId={stage.outboxItemId}
						kind={kind}
					/>
				);
			case 'interrupted':
				return <InterruptedRecordingBanner subject={subject} outboxItem={stage.outboxItem} />;
			case 'transferring':
				return <TransferCard outboxItem={stage.outboxItem} />;
			case 'awaiting-server':
				return <AwaitingServerCard />;
			case 'received':
				return (
					<UploadEmptyState
						subject={subject}
						title="L'enregistrement est bien arrivé."
						description="Ajoutez-le à une préparation du dossier pour le transcrire et l'utiliser."
						icon={<CheckCircle2 size={28} />}
						canReturn
					/>
				);
			case 'uploading-elsewhere':
				return (
					<UploadEmptyState
						subject={subject}
						title="L'enregistrement est en cours d'envoi depuis un autre appareil."
						description="Cette page se mettra à jour dès sa réception."
						icon={<UploadCloud size={28} />}
						canReturn={false}
					/>
				);
			case 'no-recording':
				return (
					<UploadEmptyState
						subject={subject}
						title="L'enregistrement a été supprimé."
						icon={<MicOff size={28} />}
						canReturn
					/>
				);
			case 'no-workflow':
				return (
					<UploadEmptyState
						subject={subject}
						title="La transcription automatique est désactivée."
						description="L'enregistrement est conservé : ajoutez-le à une préparation du dossier pour le transcrire."
						icon={<Settings2 size={28} />}
						canReturn
					/>
				);
			case 'not-found':
				// A failed read is not a missing interview: it can be retried.
				return error !== null ? (
					<ErrorState title="Impossible de charger l'enregistrement" onRetry={refetch} />
				) : (
					<UploadEmptyState
						subject={subject}
						title="Enregistrement introuvable"
						description="Cet enregistrement n'existe pas ou n'appartient pas à ce dossier."
						icon={<SearchX size={28} />}
						canReturn
					/>
				);
		}
	};

	return (
		<React.Fragment>
			<PageHeader eyebrow="ENVOI DE L'ENREGISTREMENT" title={title} description={participants} />
			{renderStageCard()}
		</React.Fragment>
	);
};

export default InterviewUploadContent;
